import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getProfilingTemplate, createProfilingTemplate, updateProfilingTemplate } from '../services/api';
import Modal from '../components/Modal';
import { useDialog } from '../components/DialogProvider';
import {
  Box, Typography, Button, IconButton, TextField, Paper, Stack, Alert, Chip,
  CircularProgress, Checkbox, FormControlLabel, MenuItem, DialogActions
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

const FIELD_TYPES = ['text', 'textarea', 'number', 'select', 'boolean', 'date'];

function safeParse(val, fallback) {
  if (val === null || val === undefined) return fallback;
  if (typeof val !== 'string') return val;
  try { return JSON.parse(val); } catch { return fallback; }
}

// ── Main Page ──

export default function ProfilingTemplatePage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const dialog = useDialog();
  const isNew = !id || id === 'new';

  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // ── Form state ──
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [isDefault, setIsDefault] = useState(false);
  const [sections, setSections] = useState([]); // [{ section: 'Name', fields: [{ key, label, type }] }]

  // ── Modal state ──
  const [modal, setModal] = useState(null);
  // modal types: 'add-section' | 'edit-section' | 'add-field' | 'edit-field'
  const [modalSectionName, setModalSectionName] = useState('');
  const [modalField, setModalField] = useState({ key: '', label: '', type: 'text' });

  // ── Load existing template ──
  const loadTemplate = useCallback(async () => {
    try {
      const { data } = await getProfilingTemplate(id);
      setName(data.name || '');
      setDescription(data.description || '');
      setIsDefault(data.isDefault || false);
      const parsed = safeParse(data.schema, []);
      setSections(Array.isArray(parsed) ? parsed : (parsed.sections || []));
    } catch {
      setError('Failed to load profiling template');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (!isNew) loadTemplate();
  }, [isNew, loadTemplate]);

  // ── Section handlers ──
  const openAddSection = () => {
    setModalSectionName('');
    setModal({ type: 'add-section' });
  };

  const openEditSection = (secIdx) => {
    setModalSectionName(sections[secIdx].section);
    setModal({ type: 'edit-section', secIdx });
  };

  const handleSaveSection = () => {
    if (!modalSectionName.trim()) return;
    if (modal.type === 'add-section') {
      setSections(prev => [...prev, { section: modalSectionName.trim(), fields: [] }]);
    } else {
      setSections(prev => prev.map((s, i) =>
        i === modal.secIdx ? { ...s, section: modalSectionName.trim() } : s
      ));
    }
    setModal(null);
  };

  const handleDeleteSection = async (secIdx) => {
    if (!(await dialog.confirm({ title: 'Delete section', message: `Delete section "${sections[secIdx].section}" and all its fields?`, confirmText: 'Delete', danger: true }))) return;
    setSections(prev => prev.filter((_, i) => i !== secIdx));
  };

  const moveSectionUp = (secIdx) => {
    if (secIdx === 0) return;
    setSections(prev => {
      const arr = [...prev];
      [arr[secIdx - 1], arr[secIdx]] = [arr[secIdx], arr[secIdx - 1]];
      return arr;
    });
  };

  const moveSectionDown = (secIdx) => {
    setSections(prev => {
      if (secIdx >= prev.length - 1) return prev;
      const arr = [...prev];
      [arr[secIdx], arr[secIdx + 1]] = [arr[secIdx + 1], arr[secIdx]];
      return arr;
    });
  };

  // ── Field handlers ──
  const openAddField = (secIdx) => {
    setModalField({ key: '', label: '', type: 'text' });
    setModal({ type: 'add-field', secIdx });
  };

  const openEditField = (secIdx, fieldIdx) => {
    const f = sections[secIdx].fields[fieldIdx];
    setModalField({ key: f.key || '', label: f.label || '', type: f.type || 'text' });
    setModal({ type: 'edit-field', secIdx, fieldIdx });
  };

  const handleSaveField = () => {
    if (!modalField.key.trim() || !modalField.label.trim()) return;
    const field = { key: modalField.key.trim(), label: modalField.label.trim(), type: modalField.type };
    setSections(prev => prev.map((s, i) => {
      if (i !== modal.secIdx) return s;
      const fields = [...s.fields];
      if (modal.type === 'edit-field') {
        fields[modal.fieldIdx] = field;
      } else {
        fields.push(field);
      }
      return { ...s, fields };
    }));
    setModal(null);
  };

  const handleDeleteField = (secIdx, fieldIdx) => {
    setSections(prev => prev.map((s, i) =>
      i !== secIdx ? s : { ...s, fields: s.fields.filter((_, fi) => fi !== fieldIdx) }
    ));
  };

  const moveFieldUp = (secIdx, fieldIdx) => {
    if (fieldIdx === 0) return;
    setSections(prev => prev.map((s, i) => {
      if (i !== secIdx) return s;
      const fields = [...s.fields];
      [fields[fieldIdx - 1], fields[fieldIdx]] = [fields[fieldIdx], fields[fieldIdx - 1]];
      return { ...s, fields };
    }));
  };

  const moveFieldDown = (secIdx, fieldIdx) => {
    setSections(prev => prev.map((s, i) => {
      if (i !== secIdx) return s;
      if (fieldIdx >= s.fields.length - 1) return s;
      const fields = [...s.fields];
      [fields[fieldIdx], fields[fieldIdx + 1]] = [fields[fieldIdx + 1], fields[fieldIdx]];
      return { ...s, fields };
    }));
  };

  // ── Save template ──
  const handleSave = async () => {
    if (!name.trim()) { setError('Name is required'); return; }
    setError('');
    setSaving(true);
    try {
      const payload = {
        name: name.trim(),
        description: description.trim(),
        isDefault,
        schema: sections,
      };
      if (isNew) {
        await createProfilingTemplate(payload);
      } else {
        await updateProfilingTemplate(id, payload);
      }
      navigate('/management?tab=profiling');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save profiling template');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Box sx={{ p: 5, textAlign: 'center' }}><CircularProgress /></Box>;

  const totalFields = sections.reduce((sum, s) => sum + s.fields.length, 0);

  return (
    <Box>
      {/* Back link + header */}
      <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/management?tab=profiling')} sx={{ mb: 2 }}>
        Back to Management
      </Button>

      <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>{isNew ? 'New Profiling Template' : 'Edit Profiling Template'}</Typography>
          <Typography variant="body2" color="text.secondary">Define the profile schema used when profiling customers — sections and fields rendered dynamically.</Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Button variant="outlined" onClick={() => navigate('/management?tab=profiling')}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : isNew ? 'Create Template' : 'Save Changes'}
          </Button>
        </Stack>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* ── Section 1: Basic Information ── */}
      <Paper sx={{ p: 3, mb: 2.5 }}>
        <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
          <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <span>📋</span> Basic Information
          </Typography>
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>Identity and role of this profiling template.</Typography>
        <Stack spacing={2}>
          <TextField size="small" fullWidth label="Name *" required value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Default B2B Profile" />
          <TextField size="small" fullWidth multiline rows={2} label="Description" value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Brief description of this template" />
          <Box>
            <FormControlLabel control={<Checkbox checked={isDefault} onChange={(e) => setIsDefault(e.target.checked)} />} label="Set as Default Template" />
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', ml: 4 }}>The default template is applied automatically to new customers.</Typography>
          </Box>
        </Stack>
      </Paper>

      {/* ── Schema Sections ── */}
      <Paper sx={{ p: 3, mb: 2.5 }}>
        <Stack direction="row" sx={{ justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
          <Box>
            <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <span>🧩</span> Schema Sections
              <Chip size="small" label={`${sections.length} sections · ${totalFields} fields`} />
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Define sections and their fields. Each section becomes a group in the customer profile editor.
            </Typography>
          </Box>
          <Button variant="contained" size="small" onClick={openAddSection}>+ Add Section</Button>
        </Stack>

        {sections.length === 0 ? (
          <Typography variant="body2" color="text.disabled" sx={{ textAlign: 'center', py: 4 }}>
            No sections defined yet. Add a section to start building the schema.
          </Typography>
        ) : (
          <Stack spacing={2}>
            {sections.map((sec, secIdx) => (
              <Paper key={secIdx} variant="outlined" sx={{ mb: 0, overflow: 'hidden' }}>
                {/* Section header */}
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: '12px 16px', bgcolor: 'grey.50', borderBottom: '1px solid', borderColor: 'divider' }}>
                  <Stack direction="row" sx={{ alignItems: 'center' }} spacing={1}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>{sec.section}</Typography>
                    <Chip size="small" label={`${sec.fields.length} fields`} />
                  </Stack>
                  <Stack direction="row" spacing={0.5}>
                    <IconButton size="small" onClick={() => moveSectionUp(secIdx)} disabled={secIdx === 0} title="Move up">↑</IconButton>
                    <IconButton size="small" onClick={() => moveSectionDown(secIdx)} disabled={secIdx === sections.length - 1} title="Move down">↓</IconButton>
                    <Button variant="outlined" size="small" onClick={() => openEditSection(secIdx)}>Rename</Button>
                    <Button variant="contained" size="small" onClick={() => openAddField(secIdx)}>+ Field</Button>
                    <Button variant="outlined" color="error" size="small" onClick={() => handleDeleteSection(secIdx)}>Delete</Button>
                  </Stack>
                </Box>

                {/* Fields */}
                <Box sx={{ p: sec.fields.length > 0 ? '10px 16px' : '0 16px' }}>
                  {sec.fields.length === 0 ? (
                    <Typography variant="body2" color="text.disabled" sx={{ textAlign: 'center', py: 2 }}>
                      No fields in this section
                    </Typography>
                  ) : (
                    <Stack spacing={0.5}>
                      {sec.fields.map((f, fieldIdx) => (
                        <Paper key={fieldIdx} variant="outlined" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 1 }}>
                          <Stack direction="row" sx={{ flex: 1, alignItems: 'center' }} spacing={1.5}>
                            <Typography variant="body2" sx={{ fontWeight: 500, minWidth: 100 }}>{f.label}</Typography>
                            <Typography variant="caption" color="text.disabled">key: {f.key}</Typography>
                            <Chip size="small" label={f.type} color="default" />
                          </Stack>
                          <Stack direction="row" spacing={0.5} sx={{ flexShrink: 0 }}>
                            <IconButton size="small" onClick={() => moveFieldUp(secIdx, fieldIdx)} disabled={fieldIdx === 0} title="Move up">↑</IconButton>
                            <IconButton size="small" onClick={() => moveFieldDown(secIdx, fieldIdx)} disabled={fieldIdx === sec.fields.length - 1} title="Move down">↓</IconButton>
                            <Button variant="outlined" size="small" onClick={() => openEditField(secIdx, fieldIdx)}>Edit</Button>
                            <Button variant="outlined" color="error" size="small" onClick={() => handleDeleteField(secIdx, fieldIdx)}>Delete</Button>
                          </Stack>
                        </Paper>
                      ))}
                    </Stack>
                  )}
                </Box>
              </Paper>
            ))}
          </Stack>
        )}
      </Paper>

      {/* ── Bottom save bar ── */}
      <Paper elevation={3} sx={{ position: 'sticky', bottom: 0, p: 2, mt: 3, borderTop: '1px solid', borderColor: 'divider', display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
        <Button variant="outlined" onClick={() => navigate('/management?tab=profiling')}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving}>
          {saving ? 'Saving…' : isNew ? 'Create Template' : 'Save Changes'}
        </Button>
      </Paper>

      {/* ── Section Name Modal ── */}
      {modal && (modal.type === 'add-section' || modal.type === 'edit-section') && (
        <Modal
          title={modal.type === 'add-section' ? 'Add Section' : 'Rename Section'}
          onClose={() => setModal(null)}
        >
          <Stack spacing={2} sx={{ mb: 2 }}>
            <TextField
              size="small"
              fullWidth
              label="Section Name *"
              value={modalSectionName}
              onChange={(e) => setModalSectionName(e.target.value)}
              placeholder="e.g. Company Info, Decision Process, Technology"
              autoFocus
            />
          </Stack>
          <DialogActions sx={{ px: 0 }}>
            <Button variant="outlined" onClick={() => setModal(null)}>Cancel</Button>
            <Button variant="contained" onClick={handleSaveSection} disabled={!modalSectionName.trim()}>
              {modal.type === 'add-section' ? 'Add' : 'Save'}
            </Button>
          </DialogActions>
        </Modal>
      )}

      {/* ── Field Modal ── */}
      {modal && (modal.type === 'add-field' || modal.type === 'edit-field') && (
        <Modal
          title={modal.type === 'add-field' ? 'Add Field' : 'Edit Field'}
          onClose={() => setModal(null)}
        >
          <Stack spacing={2} sx={{ mb: 2 }}>
            <Box>
              <TextField
                size="small"
                fullWidth
                label="Key *"
                value={modalField.key}
                onChange={(e) => setModalField({ ...modalField, key: e.target.value })}
                placeholder="e.g. website, companySize, techStack"
                autoFocus
              />
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>Unique identifier used internally (camelCase recommended).</Typography>
            </Box>
            <Box>
              <TextField
                size="small"
                fullWidth
                label="Label *"
                value={modalField.label}
                onChange={(e) => setModalField({ ...modalField, label: e.target.value })}
                placeholder="e.g. Website, Company Size, Tech Stack"
              />
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>Display name shown to users in the profile editor.</Typography>
            </Box>
            <TextField
              select
              size="small"
              fullWidth
              label="Type *"
              value={modalField.type}
              onChange={(e) => setModalField({ ...modalField, type: e.target.value })}
            >
              {FIELD_TYPES.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
            </TextField>
          </Stack>
          <DialogActions sx={{ px: 0 }}>
            <Button variant="outlined" onClick={() => setModal(null)}>Cancel</Button>
            <Button variant="contained" onClick={handleSaveField} disabled={!modalField.key.trim() || !modalField.label.trim()}>
              {modal.type === 'add-field' ? 'Add Field' : 'Save'}
            </Button>
          </DialogActions>
        </Modal>
      )}
    </Box>
  );
}
